import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import BrandLogo from '@/components/ui/BrandLogo';

const { FiGrid, FiLayers, FiList, FiSettings, FiPlayCircle } = FiIcons;

export default function AppShell({ children, currentTab, setCurrentTab }) {
  const navItems = [
    { id: 'dashboard', label: 'Cockpit', icon: FiGrid },
    { id: 'envelopes', label: 'Envelopes', icon: FiLayers },
    { id: 'transactions', label: 'Ledger', icon: FiList },
    { id: 'demo', label: 'Showcase', icon: FiPlayCircle },
    { id: 'settings', label: 'Config', icon: FiSettings },
  ];

  return (
    <div className="flex h-screen w-full bg-bb-dark overflow-hidden selection:bg-bb-gold selection:text-bb-dark">
      
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-bb-glassBorder bg-bb-panel/50 backdrop-blur-md z-20">
        <div className="p-8">
          <BrandLogo />
        </div>

        <nav className="flex-1 px-4 py-2 space-y-1.5">
          <p className="text-[10px] font-mono text-gray-600 uppercase tracking-[0.3em] pl-4 mb-4">Core Modules</p>
          {navItems.map((item) => {
            const isActive = currentTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-300 group ${
                  isActive 
                    ? 'bg-bb-gold/10 text-bb-gold border border-bb-gold/20 shadow-[0_0_20px_rgba(212,175,55,0.05)]' 
                    : 'text-gray-500 hover:bg-white/5 hover:text-white border border-transparent'
                }`}
              >
                <SafeIcon icon={item.icon} className={isActive ? 'text-bb-gold scale-110' : 'group-hover:scale-110 transition-transform'} />
                <span className="font-medium text-sm tracking-tight">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-1 h-4 rounded-full bg-bb-gold shadow-[0_0_8px_rgba(212,175,55,1)]"></div>
                )}
              </button>
            )
          })}
        </nav>

        <div className="p-6 mt-auto">
          <div className="bg-bb-dark/40 rounded-2xl p-4 border border-white/5 backdrop-blur-sm group cursor-help">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
              <p className="text-[10px] text-gray-500 font-mono tracking-widest uppercase">System Credit</p>
            </div>
            <p className="text-[11px] text-gray-400 font-medium group-hover:text-bb-gold transition-colors">
              Made by <span className="text-white">LingLink AI</span> & <span className="text-white">DigLab AI</span>
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col relative h-full">
        {/* Mobile Header */}
        <header className="md:hidden flex items-center justify-between p-4 border-b border-bb-glassBorder bg-bb-panel/80 backdrop-blur-md z-20">
          <BrandLogo size="md" />
          <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-bb-gold">
            <SafeIcon icon={FiSettings} />
          </div>
        </header>

        {/* Scrollable Content */}
        <div id="main-scroll" className="flex-1 overflow-y-auto p-4 md:p-10 z-10 relative">
          {/* Subtle Grid Background */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(212,175,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
          
          <div className="max-w-6xl mx-auto relative z-10 pb-24 md:pb-0">
            {children}
          </div>
        </div>

        {/* Mobile Bottom Nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-bb-panel/90 backdrop-blur-xl border-t border-bb-glassBorder pb-safe z-30">
          <div className="flex justify-around items-center h-18 px-2 py-2">
            {navItems.map((item) => {
              const isActive = currentTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setCurrentTab(item.id)}
                  className={`flex flex-col items-center justify-center w-16 h-full space-y-1 transition-all ${
                    isActive ? 'text-bb-gold' : 'text-gray-500'
                  }`}
                >
                  <div className={`p-2 rounded-lg transition-colors ${isActive ? 'bg-bb-gold/10' : ''}`}>
                    <SafeIcon icon={item.icon} size={20} />
                  </div>
                  <span className="text-[9px] font-mono uppercase tracking-tighter">{item.label}</span>
                </button>
              )
            })}
          </div>
        </nav>
      </main>
    </div>
  );
}